import React, { useEffect, useMemo, useState } from "react";
import { Handshake, Plus } from "lucide-react";
import {
    PARTNER_ROLES, PARTNER_SPECIALTIES, createPartner, deletePartner, getPartnerAdminDetail, listPartners,
    removePartnerAssignment, setPartnerStatus, updatePartner,
    type PartnerAssignment, type PartnerInput, type PartnerProfile, type PartnerRole,
} from "../../Lib/partnerApi";
import { sharePartnerInvitation, type InvitationMethod } from "./partnerInvitations";
import { PartnerDetail, PartnerEditor, PartnerFilter, PartnerRow } from "./PartnerRoleComponents";
import "./partners.css";
type PartnerForm = PartnerInput;
const blankForm = (): PartnerForm => ({
    name: "", email: "", mobile: "", currentCompany: "", professionalTitle: "",
    bio: "", photoURL: "", roles: [], specialties: [], interviewRoundTypes: [],
});
const errorText = (cause: unknown) => cause instanceof Error ? cause.message : "That action could not be completed.";
const availablePartnerRoles = PARTNER_ROLES;
export default function PartnersTab() {
    const [partners, setPartners] = useState<PartnerProfile[]>([]);
    const [selected, setSelected] = useState<PartnerProfile | null>(null);
    const [assignments, setAssignments] = useState<PartnerAssignment[]>([]);
    const [form, setForm] = useState<PartnerForm>(blankForm());
    const [editorOpen, setEditorOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("ALL");
    const [roleFilter, setRoleFilter] = useState("ALL");
    const [specialtyFilter, setSpecialtyFilter] = useState("ALL");
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");
    async function refresh() { setPartners(await listPartners()); }
    useEffect(() => { refresh().catch((cause) => setError(errorText(cause))); }, []);
    const filtered = useMemo(() => partners.filter((partner) => {
        const haystack = `${partner.name} ${partner.email} ${partner.mobile || ""} ${partner.currentCompany || ""}`.toLowerCase();
        return haystack.includes(search.toLowerCase())
            && (statusFilter === "ALL" || partner.status === statusFilter)
            && (roleFilter === "ALL" || partner.roles.includes(roleFilter as PartnerRole))
            && (specialtyFilter === "ALL" || partner.specialties.includes(specialtyFilter));
    }), [partners, roleFilter, search, specialtyFilter, statusFilter]);
    async function openPartner(partner: PartnerProfile) {
        setError(""); setMessage(""); setSelected(partner);
        try { setAssignments((await getPartnerAdminDetail(partner.id)).assignments); }
        catch (cause) { setError(errorText(cause)); }
    }
    function edit(partner?: PartnerProfile) {
        setError(""); setMessage(""); setSelected(partner || null);
        setForm(partner ? {
            name: partner.name, email: partner.email, mobile: partner.mobile || "",
            currentCompany: partner.currentCompany || "", professionalTitle: partner.professionalTitle || "",
            bio: partner.bio || "", photoURL: partner.photoURL || "", roles: partner.roles,
            specialties: partner.specialties, interviewRoundTypes: partner.interviewRoundTypes,
        } : blankForm());
        setEditorOpen(true);
    }
    async function savePartner(event: React.FormEvent) {
        event.preventDefault(); setBusy(true); setError("");
        try {
            if (!form.roles.length) throw new Error("Select at least one partner role.");
            if (selected) await updatePartner({ partnerId: selected.id, partner: form });
            else {
                const result = await createPartner(form);
                await navigator.clipboard?.writeText(result.invitationUrl);
                setMessage("Partner invitation created. The activation link was copied to your clipboard.");
            }
            setEditorOpen(false); setSelected(null); await refresh();
        } catch (cause) { setError(errorText(cause)); }
        finally { setBusy(false); }
    }
    async function changeStatus(partner: PartnerProfile, status: "ACTIVE" | "INACTIVE") {
        setBusy(true); setError("");
        try {
            await setPartnerStatus({ partnerId: partner.id, status }); await refresh();
            if (selected?.id === partner.id) await openPartner({ ...partner, status });
        } catch (cause) { setError(errorText(cause)); }
        finally { setBusy(false); }
    }
    async function removePartner(partner: PartnerProfile) {
        if (!window.confirm(`Permanently delete ${partner.name}? This removes their invitation, account link, and unused slots. This cannot be undone.`)) return;
        setBusy(true); setError(""); setMessage("");
        try {
            await deletePartner(partner.id); setSelected(null); await refresh();
            setMessage(`${partner.name} was permanently deleted.`);
        } catch (cause) { setError(errorText(cause)); }
        finally { setBusy(false); }
    }
    async function shareInvitation(partner: PartnerProfile, method: InvitationMethod) {
        setBusy(true); setError("");
        try { setMessage(await sharePartnerInvitation(partner, method)); await refresh(); }
        catch (cause) { setError(errorText(cause)); }
        finally { setBusy(false); }
    }
    async function removeAssignment(assignment: PartnerAssignment) {
        if (!selected) return;
        setBusy(true); setError("");
        try { await removePartnerAssignment({ partnerId: selected.id, assignmentId: assignment.id }); await openPartner(selected); }
        catch (cause) { setError(errorText(cause)); }
        finally { setBusy(false); }
    }
    return <div className="partner-admin space-y-5">
        <div className="partner-card rounded-3xl p-5 sm:p-7">
            <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
                <div><span className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-xs font-black uppercase tracking-[0.12em] text-blue-700"><Handshake className="h-4 w-4" />Partner network</span><h2 className="mt-3 text-2xl font-black text-[#061a4a]">Trusted partners</h2><p className="mt-1 text-sm text-slate-600">Invite mentors and interviewers, then control exactly which work each partner can perform.</p></div>
                <button onClick={() => edit()} className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#075dcc] to-[#38aee8] px-4 py-2.5 text-sm font-bold text-white shadow-lg shadow-blue-500/20"><Plus size={17} />Add partner</button>
            </div>
            <div className="mt-6 grid gap-3 rounded-2xl bg-blue-50/70 p-3 md:grid-cols-4">
                <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search name, email, mobile…" className="rounded-xl border px-3 py-2.5 text-sm" />
                <PartnerFilter value={statusFilter} onChange={setStatusFilter} values={["ALL", "INVITED", "ACTIVE", "INACTIVE"]} />
                <PartnerFilter value={roleFilter} onChange={setRoleFilter} values={["ALL", ...availablePartnerRoles]} roleLabels />
                <PartnerFilter value={specialtyFilter} onChange={setSpecialtyFilter} values={["ALL", ...PARTNER_SPECIALTIES]} />
            </div>
            {(error || message) && <p className={`mt-4 rounded-xl px-3 py-2 text-sm font-semibold ${error ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"}`}>{error || message}</p>}
            <div className="mt-5 grid gap-3">
                {filtered.map((partner) => <PartnerRow key={partner.id} partner={partner} busy={busy} onOpen={() => openPartner(partner)} onEdit={() => edit(partner)} onCopy={() => shareInvitation(partner, "copy")} onEmail={() => shareInvitation(partner, "email")} onStatus={(status) => changeStatus(partner, status)} />)}
                {!filtered.length && <p className="rounded-2xl border border-dashed border-blue-200 bg-blue-50/40 py-10 text-center text-sm text-slate-500">No partners match these filters.</p>}
            </div>
        </div>
        {selected && !editorOpen && <PartnerDetail partner={selected} assignments={assignments} busy={busy} onClose={() => setSelected(null)} onEdit={() => edit(selected)} onCopyInvite={() => shareInvitation(selected, "copy")} onEmailInvite={() => shareInvitation(selected, "email")} onStatus={(status) => changeStatus(selected, status)} onDelete={() => removePartner(selected)} onRemove={removeAssignment} />}
        {editorOpen && <PartnerEditor form={form} setForm={setForm} isEditing={Boolean(selected)} busy={busy} onClose={() => setEditorOpen(false)} onSubmit={savePartner} />}
    </div>;
}
